import React from "react";

import { Grid, MenuItem } from "@mui/material";
import { Divider } from "@mui/material";
import { useTranslation } from "react-i18next";

import { Customer } from "../../../../../../models/customer";
import { Shipment } from "../../../../../../models/shipments";
import { ShipmentDetails } from "../../../../../../util/enums";
import { Input, Select } from "../../../../../Form";
import AutoSuggest from "../../../../../Form/AutoSuggest";
import SkeletonComponent from "../../../../../Generic/Skeleton";
import getShipmentOptions from "../../../../../Shipments/hooks/getShipmentOptions";
import { DetailsSectionsProps, Options } from "../../../../../Shipments/types";
import useStyles from "../../styles";
import { DetailsSectionTitle } from "../SectionTitle";

interface DetailsGeneralSectionProps extends DetailsSectionsProps {
  onUpdateAllShipment: (value: Shipment) => void;
  loadingResourcesByVersion: boolean;
  customerSelected: Customer | undefined;
  customerList: Customer[];
}

export const DetailsConsigneeSection = ({
  generateBaseFieldProps,
  getErrorByField,
  fieldHasError,
  onUpdateShipmentDetails,
  viewOnly,
  data,
  loadingResourcesByVersion,
  customerSelected,
  customerList,
}: DetailsGeneralSectionProps) => {
  const { t: translate } = useTranslation();
  const { serviceTypeOptions, boundOptions, deliverToOptions } = getShipmentOptions(false);
  const classes = useStyles();

  return (
    <Grid container item spacing={2}>
      <Grid item xs={12}>
        <Divider orientation={"horizontal"} flexItem className={classes.dividerHorizontal} />
      </Grid>
      <DetailsSectionTitle title={translate("consignee")} />
      <Grid container item spacing={2}>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <SkeletonComponent isInput isLoading={loadingResourcesByVersion}>
            <AutoSuggest
              name={ShipmentDetails.CONSIGNEE_ID}
              label={translate("consignee")}
              placeholder={translate("acmeClothing")}
              inputValue={customerSelected?.fullName || ""}
              options={customerList}
              getOptionSelected={(option?: Customer, value?: Customer) => option?.id === value?.id}
              getOptionLabel={(customer: Customer) => customer.fullName || ""}
              onChange={(customer: Customer) => {
                if (customer && customer.id) {
                  onUpdateShipmentDetails(ShipmentDetails.CONSIGNEE_ID, customer.id);
                } else {
                  onUpdateShipmentDetails(ShipmentDetails.CONSIGNEE_ID, null);
                }
              }}
              disabled={viewOnly}
              error={fieldHasError(ShipmentDetails.CONSIGNEE_ID)}
              errorMessage={getErrorByField(ShipmentDetails.CONSIGNEE_ID)}
              dataCy={ShipmentDetails.CONSIGNEE_ID}
              required
            />
          </SkeletonComponent>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <SkeletonComponent isInput isLoading={loadingResourcesByVersion}>
            <Input
              {...generateBaseFieldProps(ShipmentDetails.CONSIGNEE_CODE)}
              value={customerSelected?.code || ""}
              disabled={true}
              placeholder="123413"
            />
          </SkeletonComponent>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Select
            name={ShipmentDetails.SERVICE_TYPE}
            displayEmpty
            placeholder={translate("selectOne")}
            label={translate(ShipmentDetails.SERVICE_TYPE)}
            value={data?.serviceType || ""}
            disabled={viewOnly}
            onChange={(event) =>
              onUpdateShipmentDetails(ShipmentDetails.SERVICE_TYPE, event.target.value)
            }
            error={fieldHasError(ShipmentDetails.SERVICE_TYPE)}
            errorMessage={getErrorByField(ShipmentDetails.SERVICE_TYPE)}
            dataCy={ShipmentDetails.SERVICE_TYPE}
          >
            {serviceTypeOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Select
            {...generateBaseFieldProps(ShipmentDetails.BOUND)}
            displayEmpty
            placeholder={translate("selectOne")}
            label={translate("shipment")}
            value={data?.bound || ""}
            onChange={(event) => onUpdateShipmentDetails(ShipmentDetails.BOUND, event.target.value)}
          >
            {boundOptions.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Grid>
        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Select
            {...generateBaseFieldProps(ShipmentDetails.DELIVER_TO)}
            defaultValue=""
            displayEmpty
            placeholder={translate("port")}
            value={data?.deliverTo}
            onChange={(event) =>
              onUpdateShipmentDetails(ShipmentDetails.DELIVER_TO, event.target.value)
            }
          >
            {deliverToOptions &&
              deliverToOptions.map((option: Options) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
          </Select>
        </Grid>

        <Grid item xs={12} sm={6} md={3} lg={3}>
          <Input
            {...generateBaseFieldProps(ShipmentDetails.CARRIER)}
            value={data?.carrier || ""}
            onChange={(event) =>
              onUpdateShipmentDetails(ShipmentDetails.CARRIER, event.target.value)
            }
          />
        </Grid>
      </Grid>
    </Grid>
  );
};
